import { api } from './api';

const MAPKIT_SCRIPT_URL = 'https://cdn.apple-mapkit.com/mk/5.78.132/mapkit.js';

let scriptPromise: Promise<void> | null = null;
let initPromise: Promise<void> | null = null;
let initFailed = false;

function loadMapKitScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('MapKit JS is only available in the browser.'));
  }

  if (window.mapkit) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-mapkit-js="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('MapKit JS failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = MAPKIT_SCRIPT_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.mapkitJs = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('MapKit JS failed to load.'));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

async function fetchMapKitToken() {
  const response = await api.get<string>('/mapkit/token', {
    responseType: 'text',
    transformResponse: [(data) => data],
  });
  const token = response.data.trim();
  if (!token) {
    throw new Error('MapKit JS token was empty.');
  }
  return token;
}

export function mapkitAuthHasFailed() {
  return initFailed;
}

export function loadMapKit() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('MapKit JS is only available in the browser.'));
  }

  if (window.mapkit?.Map) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = loadMapKitScript()
    .then(
      () =>
        new Promise<void>((resolve, reject) => {
          try {
            mapkit.init({
              authorizationCallback: (done: (token: string) => void) => {
                void fetchMapKitToken()
                  .then((token) => {
                    initFailed = false;
                    done(token);
                  })
                  .catch((error) => {
                    initFailed = true;
                    reject(error instanceof Error ? error : new Error('MapKit JS authorization failed.'));
                  });
              },
            });
            resolve();
          } catch (error) {
            initFailed = true;
            reject(error instanceof Error ? error : new Error('MapKit JS initialization failed.'));
          }
        }),
    )
    .catch((error) => {
      initFailed = true;
      initPromise = null;
      throw error;
    });

  return initPromise;
}

export function mapkitMissingConfigHint() {
  return 'Apple Maps is not configured on the API. Add MAPKIT_TEAM_ID, MAPKIT_KEY_ID, and MAPKIT_PRIVATE_KEY to apps/api/.env, then restart the API.';
}
